import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ListFilter, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { ONE_SHOT_HIGHLIGHT_CLASS, ONE_SHOT_HIGHLIGHT_TRANSITION } from '@/hooks/useOneShotHighlight';
import { cn } from '@/lib/utils';
import { billReturnState, buildSetPriceHref } from '@/features/client-prices/lib/client-prices-url';
import { CountCell, MoneyCell } from '@/components/shared/NumberCells';
import { INLINE_ACTION_CLASS, NO_PRICE_FOCUS, Swatch } from './ThreePlCells';
import { ThreePlUsageTip, type ThreePlUsageTipName } from './ThreePlUsageTip';
import {
  billReturnUrl,
  formatUsageDay,
  type BillDetail,
  type BillExtraDetail,
  type BillExtraRowView,
  type BillLineId,
  type BillLineView,
  type BillNotOffered,
  type BillPrice,
  type BillRowView,
  type BillUnpriced,
  type BillView,
  type ScopedClient,
  type StockPart,
} from '../lib/three-pl-usage-view';

/**
 * The 3PL's bill-line controls (owner `skus.costs_manage` holders only, D28):
 * Add a service to this bill and the remove control on each hand-added line.
 * Null for every other viewer, so neither control is ever in their DOM. Keep
 * it referentially stable: the bill is memoised.
 */
export interface BillLineActions {
  /** False when this client has no service of the 3PL's to add: the button is disabled, with a hint. */
  canAdd: boolean;
  onAdd: () => void;
  /** `trigger` is the row's remove button, so focus can go back to it if the line is kept. */
  onRemove: (row: BillExtraRowView, trigger: HTMLButtonElement) => void;
  /** Add a service to this bill, so focus can land on it once a removed row is gone. */
  addButtonRef: React.RefObject<HTMLButtonElement>;
}

interface ThreePlBillProps {
  client: ScopedClient;
  bill: BillView;
  ownerName: string;
  orders: number;
  topUps: number;
  /** Production redos (3PL only); null for a client viewer, never rendered then. */
  redone: number | null;
  /** Formats in the report's currency (D2), never the viewer's. */
  formatValue: (value: number) => string;
  /** The bill line filtering the SKU table, if any. */
  selectedLine: BillLineId | null;
  onToggleLine: (line: BillLineId) => void;
  /** Back to All clients (3PL only); absent for a client viewer. */
  onBack: (() => void) | null;
  /** Owner `skus.costs_manage` holders only (D28); null for everyone else. */
  actions: BillLineActions | null;
  /**
   * Where the page just jumped, tinted once (DESIGN.md §5 one-shot
   * highlight): a row's `key` (the total panel's "Not final" note) or a line's
   * anchor (Back to bill, `BILL_FOCUS_PARAM`: every row of that line); null
   * otherwise.
   */
  highlightedRow: string | null;
}

// Below `sm` the cells pad less and the number columns take only their
// content's width, so Charge keeps room for whole words and the four columns
// fit a phone without the bill scrolling sideways. `sm` and up keep the
// roomier padding and the fixed number column widths.
const NUMBER_TD = 'px-2 py-2.5 text-right align-top sm:px-4';
const NUMBER_TH = 'whitespace-nowrap px-2 py-2 text-right text-xs font-medium text-muted-foreground sm:px-4';
/** The Charge cell: a long name breaks rather than widening the bill. */
const CHARGE_TD = 'px-3 py-2.5 [overflow-wrap:anywhere] sm:px-4';
const SECTION_TH =
  'px-3 pb-1.5 pt-4 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:px-4';
const COLUMN_COUNT = 4;
/**
 * On EVERY bill row: any row can be a landing target (the "Not final" note, Back
 * to bill), so the one-shot tint fades in and out (DESIGN.md §5), and a
 * keyboard toggle fades its selected tint in. `hover:transition-none` keeps the
 * hover tint instant (§14): under `:hover` it outranks the plain transition.
 */
const ROW_FADE = `${ONE_SHOT_HIGHLIGHT_TRANSITION} hover:transition-none`;
/** A grey line under a title: capped at a readable measure (a typed note can run 200 characters). */
const DETAIL_P = 'max-w-prose whitespace-normal text-xs text-muted-foreground';
/**
 * The lines whose rule a reader cannot see from the bill itself. The (i) sits
 * after the toggle on the line's first row only, outside the button, so the
 * toggle's name stays the title; a click on it never toggles the line.
 */
const LINE_TIPS: Partial<Record<BillLineId, ThreePlUsageTipName>> = {
  printing: 'printing',
  ownerStock: 'ownerStock',
};
/** An (i) inside a text line: -my-1 keeps the 24px target on the 20px line. */
const INLINE_TIP = '-my-1 ml-0.5';

/**
 * Set price: an in-app link to Client prices that carries this exact report
 * URL as router state, plus `ufocus=<anchor>` (P1), so the prices page can
 * offer "Back to bill" and the bill lands on the line you left from. It reads
 * the location itself (like any `Link`), so the URL is current at click time
 * (search and sort included) while the memoised bill never re-renders for it.
 * `stopPropagation`: the click never toggles the bill line.
 */
function SetPriceLink({
  to,
  label,
  anchor,
  children,
}: {
  to: string;
  label: string;
  /** The line Back to bill lands on (`BILL_FOCUS_PARAM`). */
  anchor: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  return (
    <Link
      to={to}
      state={billReturnState(billReturnUrl(location.pathname, location.search, anchor))}
      aria-label={label}
      onClick={(event) => event.stopPropagation()}
      className={INLINE_ACTION_CLASS}
    >
      {children}
    </Link>
  );
}

/**
 * One client's priced bill: Charge | Qty | Price | Amount, one value per
 * cell (D21). Each line has a bold title and grey lines under it (the stock
 * split, never a number in a number cell); a line whose price changed in the
 * period has one row per price, each with the days it covered, and its grey
 * lines move to a row of their own after the last price row, the split led by
 * the line's total ("All 10 pieces: 4 on ..., 6 on ..."), because they cover
 * every piece of the charge, not the first price's. A line with pieces is a
 * real toggle button that filters the SKU table to its SKUs (every row of the
 * line toggles the same filter); the row click is forwarded to it, so Enter
 * and Space work natively.
 *
 * A line with Qty 0 is not on the bill, and a section with no lines has no
 * heading (P3); a bill with nothing charged says so in one row. A selected
 * line shows a filter glyph beside its title (§16, never colour alone).
 *
 * Owner `skus.costs_manage` holders only (D28, `unpriced` is null for everyone
 * else): a row with no price says so in amber, with a Set price link to Client
 * prices that brings Back to bill onto the same line (P1). Garment rows show
 * "By SKU" in Price (each SKU has its own price).
 *
 * Extra services (D3, D4): the turned-on services, then the hand-added lines,
 * plain rows that never toggle. Shown when it has a row, and to an owner
 * `skus.costs_manage` holder always (it holds Add a service to this bill).
 * Those holders also get a remove control after the name of each hand-added
 * line (never on a turned-on service, which is switched off on Client prices).
 */
function ThreePlBillImpl({
  client,
  bill,
  ownerName,
  orders,
  topUps,
  redone,
  formatValue,
  selectedLine,
  onToggleLine,
  onBack,
  actions,
  highlightedRow,
}: ThreePlBillProps) {
  const { t, i18n } = useTranslation('inventory');
  const idPrefix = React.useId();
  const language = i18n.language;

  const splitText = (parts: StockPart[]): string => {
    if (parts.length === 0) return t('reporting.threePlUsage.split.none');
    const texts = parts.map((part) =>
      t('reporting.threePlUsage.split.on', {
        count: part.count,
        name: part.party === 'client' ? client.name : ownerName,
      }),
    );
    const [first = '', second] = texts;
    return second === undefined
      ? first
      : t('reporting.threePlUsage.split.pair', { first, second });
  };

  /**
   * `allPieces`: the line has several price rows, so a count list is led by
   * its total, which reads as covering every row of the line.
   */
  const detailText = (detail: BillDetail, allPieces = false): string => {
    switch (detail.kind) {
      case 'text':
        return t(detail.key);
      case 'count':
        return t(detail.key, { count: detail.count, owner: ownerName });
      case 'split':
      case 'kinds': {
        if (detail.parts.length === 0) return t('reporting.threePlUsage.split.none');
        const parts =
          detail.kind === 'split'
            ? splitText(detail.parts)
            : detail.parts
                .map((part) => t(part.key, { count: part.count }))
                .join(t('reporting.threePlUsage.listSeparator'));
        if (!allPieces) return parts;
        const count = detail.parts.reduce((sum, part) => sum + part.count, 0);
        return t('reporting.threePlUsage.bill.allPieces', { count, parts });
      }
    }
  };

  const rangeText = (range: { from: string; to: string }): string =>
    range.from === range.to
      ? formatUsageDay(range.from, language)
      : t('reporting.threePlUsage.range', {
          from: formatUsageDay(range.from, language),
          to: formatUsageDay(range.to, language),
        });

  const priceCell = (price: BillPrice) => {
    if (price.kind === 'bySku') {
      return (
        <td className={cn(NUMBER_TD, 'whitespace-nowrap text-muted-foreground')}>
          {t('reporting.threePlUsage.bill.bySku')}
        </td>
      );
    }
    return (
      <MoneyCell
        value={price.kind === 'unit' ? price.value : null}
        formatValue={formatValue}
        showZero
        className={NUMBER_TD}
      />
    );
  };

  const extraDetailText = (detail: BillExtraDetail): string => {
    switch (detail.kind) {
      case 'turnedOn':
        // Beside the row's days (C1): never "every piece" of a bill it was on for only part of.
        return detail.chargePer === 'order'
          ? t('reporting.threePlUsage.extra.everyOrder')
          : t('reporting.threePlUsage.extra.everyPiece');
      case 'note':
        return detail.text;
      case 'byHand':
        return t('reporting.threePlUsage.extra.addedByHand');
    }
  };

  /** C6: why Set price cannot reach these pieces. The SKU by code when there is one, else a count. */
  const notOfferedText = (part: BillNotOffered): string => {
    const pieces = t('reporting.threePlUsage.count.pieces', { count: part.pieces });
    return part.sku !== null
      ? t('reporting.threePlUsage.bill.notOfferedSku', { pieces, sku: part.sku })
      : t('reporting.threePlUsage.bill.notOfferedSkus', { pieces, count: part.skuCount });
  };

  /**
   * Owner `skus.costs_manage` holders only: the amber warning and, when
   * something can be priced, Set price. A garment row splits in two (C6): the
   * pieces Set price reaches, then the pieces on SKUs no longer on Client
   * prices, with the reason and no link. The wrapper is the "Not final" note's
   * focus target (C3); `anchor` is the line Set price comes back to (P1).
   */
  const unpricedNote = (unpriced: BillUnpriced, title: string, rowKey: string, anchor: string) => {
    const setPriceHref = unpriced.setPrice
      ? buildSetPriceHref({ clientOrgId: client.id, ...unpriced.setPrice })
      : null;
    const fixable = unpriced.pieces === null || unpriced.pieces > 0;
    return (
      <div data-no-price={rowKey} tabIndex={-1} className={cn('mt-1 space-y-0.5', NO_PRICE_FOCUS)}>
        {fixable && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <NoteLine tone="warning" icon={AlertTriangle}>
              {unpriced.pieces === null
                ? t('reporting.threePlUsage.bill.noPriceSet')
                : t('reporting.threePlUsage.bill.noPricePieces', { count: unpriced.pieces })}
            </NoteLine>
            {setPriceHref && (
              <SetPriceLink
                to={setPriceHref}
                label={t('reporting.threePlUsage.bill.setPriceFor', { line: title })}
                anchor={anchor}
              >
                {t('reporting.threePlUsage.bill.setPrice')}
              </SetPriceLink>
            )}
          </div>
        )}
        {unpriced.notOffered && (
          <NoteLine tone="warning" icon={AlertTriangle} className="max-w-prose [overflow-wrap:anywhere]">
            {notOfferedText(unpriced.notOffered)}
          </NoteLine>
        )}
      </div>
    );
  };

  const header = topUps > 0
    ? t('reporting.threePlUsage.bill.headerTopUps', {
        count: topUps,
        pieces: t('reporting.threePlUsage.count.pieces', { count: bill.pieces }),
        orders: t('reporting.threePlUsage.count.orders', { count: orders }),
      })
    : t('reporting.threePlUsage.bill.header', {
        pieces: t('reporting.threePlUsage.count.pieces', { count: bill.pieces }),
        orders: t('reporting.threePlUsage.count.orders', { count: orders }),
      });

  /**
   * Every row of a line toggles the same filter and shares its selected tint.
   * Every line that reaches a section has Qty above 0 (`buildBill`'s one
   * empty-row rule), so every row is a toggle.
   */
  const lineRowClass = (line: BillLineView, border: string, highlighted: boolean) =>
    cn(
      border,
      ROW_FADE,
      'cursor-pointer',
      highlighted
        ? ONE_SHOT_HIGHLIGHT_CLASS
        : selectedLine === line.id
          ? 'bg-primary/10'
          : 'hover:bg-muted/40 motion-reduce:hover:bg-muted/40',
    );

  /**
   * `withDetails`: the line's grey lines sit under this row's title (a line
   * with one price row). `border`: the rule under the row; inside a line with
   * several price rows it is dashed, so the rows read as one charge.
   */
  const renderRow = (line: BillLineView, row: BillRowView, withDetails: boolean, border: string) => {
    const selected = selectedLine === line.id;
    const title = t(line.titleKey, { owner: ownerName });
    const rangeId = `${idPrefix}-${row.key}-range`;
    const tip = row === line.rows[0] ? LINE_TIPS[line.id] : undefined;
    return (
      <tr
        key={row.key}
        onClick={() => onToggleLine(line.id)}
        className={lineRowClass(line, border, highlightedRow === row.key || highlightedRow === line.id)}
      >
        <td className={CHARGE_TD}>
          {/* Back to bill's landing target (P1): the line's first row, in document order. */}
          <button
            type="button"
            data-bill-anchor={line.id}
            aria-pressed={selected}
            aria-describedby={row.dateRange ? rangeId : undefined}
            onClick={(event) => {
              event.stopPropagation();
              onToggleLine(line.id);
            }}
            className={cn(
              'rounded-sm text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              selected && 'text-primary-strong',
            )}
          >
            {title}
            {/* Selection is never colour alone (§16): the glyph names what it does (filters the SKUs). */}
            {selected && <ListFilter className="ml-1.5 inline-block h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />}
          </button>
          {tip && <ThreePlUsageTip name={tip} values={{ owner: ownerName }} className={INLINE_TIP} />}
          {row.dateRange && (
            <p id={rangeId} className="whitespace-normal text-xs text-muted-foreground tabular-nums">
              {rangeText(row.dateRange)}
            </p>
          )}
          {withDetails &&
            line.details.map((detail, detailIndex) => (
              <p key={detailIndex} className={DETAIL_P}>
                {detailText(detail)}
              </p>
            ))}
          {row.unpriced && unpricedNote(row.unpriced, title, row.key, line.id)}
        </td>
        <CountCell value={row.qty} strong className={NUMBER_TD} />
        {priceCell(row.price)}
        <MoneyCell value={row.amount} formatValue={formatValue} className={NUMBER_TD} />
      </tr>
    );
  };

  /**
   * One price row: today's look, the grey lines under the title. Several: the
   * price rows, then ONE row holding the grey lines for the whole charge (its
   * split led by the total), so "4 on ..., 6 on ..." never sits under a row
   * whose Qty is only part of it. That row spans the table (no number cell to
   * fill) and toggles the same filter as the price rows.
   */
  const renderLine = (line: BillLineView) => {
    if (line.rows.length < 2) {
      return line.rows.map((row) => renderRow(line, row, true, 'border-b'));
    }
    const inner = 'border-b border-dashed';
    const hasDetails = line.details.length > 0;
    const rows = line.rows.map((row, index) =>
      renderRow(line, row, false, !hasDetails && index === line.rows.length - 1 ? 'border-b' : inner),
    );
    if (!hasDetails) return rows;
    return [
      ...rows,
      <tr
        key={`${line.id}:all`}
        onClick={() => onToggleLine(line.id)}
        className={lineRowClass(line, 'border-b', highlightedRow === line.id)}
      >
        <td colSpan={COLUMN_COUNT} className={cn(CHARGE_TD, 'py-2')}>
          {line.details.map((detail, detailIndex) => (
            <p key={detailIndex} className={DETAIL_P}>
              {detailText(detail, true)}
            </p>
          ))}
        </td>
      </tr>,
    ];
  };

  /**
   * An Extra services row: plain (no toggle, no hover tint), one value per
   * cell. The remove control follows the service name at every width. The
   * name is Back to bill's landing target (P1; a row with no toggle, so a
   * script-only focus target that reads the service).
   */
  const renderExtraRow = (row: BillExtraRowView) => {
    const rangeId = `${idPrefix}-${row.key}-range`;
    const detailId = `${idPrefix}-${row.key}-detail`;
    const described = [row.dateRange ? rangeId : null, row.detail ? detailId : null].filter(Boolean).join(' ');
    const highlighted = highlightedRow === row.key || highlightedRow === row.anchor;
    return (
      <tr key={row.key} className={cn('border-b', ROW_FADE, highlighted && ONE_SHOT_HIGHLIGHT_CLASS)}>
        <td className={CHARGE_TD}>
          <div className="flex items-start gap-1">
            <span
              data-bill-anchor={row.anchor}
              tabIndex={-1}
              className={cn('min-w-0 font-semibold [overflow-wrap:anywhere]', NO_PRICE_FOCUS)}
            >
              {row.serviceName}
            </span>
            {actions && row.lineId !== null && (
              // -my-1 keeps the 28px target on the 20px title line, so the row
              // and its number cells keep one geometry for every viewer.
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-my-1 h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={t('reporting.threePlUsage.extra.deleteAria', { service: row.serviceName })}
                aria-describedby={described || undefined}
                onClick={(event) => actions.onRemove(row, event.currentTarget)}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
          {row.dateRange && (
            <p id={rangeId} className="whitespace-normal text-xs text-muted-foreground tabular-nums">
              {rangeText(row.dateRange)}
            </p>
          )}
          {row.detail && (
            <p id={detailId} className={cn(DETAIL_P, '[overflow-wrap:anywhere]')}>
              {extraDetailText(row.detail)}
            </p>
          )}
          {row.unpriced && unpricedNote(row.unpriced, row.serviceName, row.key, row.anchor)}
        </td>
        <CountCell value={row.qty} strong className={NUMBER_TD} />
        {priceCell(row.price)}
        <MoneyCell value={row.amount} formatValue={formatValue} className={NUMBER_TD} />
      </tr>
    );
  };

  const addHintId = `${idPrefix}-add-hint`;

  // Never a heading over nothing (§15.2, P3): a section with no lines is left out.
  // `aria-label` keeps the heading's name to its words, not the (i)'s whole tip.
  const section = (key: string, label: string, lines: BillLineView[], tip?: ThreePlUsageTipName) =>
    lines.length === 0 ? null : (
      <tbody key={key}>
        <tr className="border-b">
          <th scope="rowgroup" colSpan={COLUMN_COUNT} className={SECTION_TH} aria-label={tip ? label : undefined}>
            {label}
            {tip && <ThreePlUsageTip name={tip} values={{ owner: ownerName }} className={INLINE_TIP} />}
          </th>
        </tr>
        {lines.map(renderLine)}
      </tbody>
    );
  // A client with nothing charged (production redos alone are the 3PL's cost).
  const nothingCharged =
    bill.sections.shipped.length === 0 && bill.sections.notShipped.length === 0 && !bill.showExtra;

  return (
    <section className="rounded-lg border bg-card">
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-3 py-3 sm:px-4">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              {t('reporting.threePlUsage.allClients')}
            </button>
          )}
          {/* The client's name in full: it wraps rather than truncating. */}
          <h3 className="flex min-w-0 items-start gap-2 text-base font-semibold">
            <span className="flex h-6 shrink-0 items-center">
              <Swatch colour={client.colour} />
            </span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{client.name}</span>
          </h3>
        </div>
        <p className="text-sm text-muted-foreground tabular-nums">{header}</p>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground sm:px-4">
                {t('reporting.threePlUsage.bill.charge')}
              </th>
              <th scope="col" className={cn(NUMBER_TH, 'sm:w-20')}>
                {t('reporting.threePlUsage.bill.qty')}
              </th>
              {/*
                The (i) leads, so "Price" keeps the right edge of the prices
                under it; `aria-label` keeps the column's name to its word.
              */}
              <th scope="col" className={cn(NUMBER_TH, 'sm:w-24')} aria-label={t('reporting.threePlUsage.bill.price')}>
                <ThreePlUsageTip name="price" align="end" className="-my-1 mr-0.5" />
                {t('reporting.threePlUsage.bill.price')}
              </th>
              <th scope="col" className={cn(NUMBER_TH, 'sm:w-28')}>
                {t('reporting.threePlUsage.bill.amount')}
              </th>
            </tr>
          </thead>
          {section('shipped', t('reporting.threePlUsage.shipped'), bill.sections.shipped)}
          {section('notShipped', t('reporting.threePlUsage.notShipped'), bill.sections.notShipped, 'notShipped')}
          {nothingCharged && (
            <tbody>
              <tr className="border-b">
                <td colSpan={COLUMN_COUNT} className="px-3 py-6 text-center text-sm text-muted-foreground sm:px-4">
                  {t('reporting.threePlUsage.empty')}
                </td>
              </tr>
            </tbody>
          )}
          {/* Shown with rows, or for an owner Admin (it holds Add a service): never empty. */}
          {bill.showExtra && (
            <tbody>
              <tr className="border-b">
                <th scope="rowgroup" colSpan={COLUMN_COUNT} className={SECTION_TH}>
                  {t('reporting.threePlUsage.bill.section.extra')}
                </th>
              </tr>
              {bill.sections.extra.map(renderExtraRow)}
              {actions && (
                <tr className="border-b">
                  <td colSpan={COLUMN_COUNT} className="px-2 py-1.5">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Button
                        ref={actions.addButtonRef}
                        type="button"
                        variant="ghost"
                        className="h-7 gap-1.5 px-2 text-xs text-primary-strong hover:bg-primary/10 hover:text-primary-strong"
                        disabled={!actions.canAdd}
                        aria-describedby={actions.canAdd ? undefined : addHintId}
                        onClick={actions.onAdd}
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('reporting.threePlUsage.extra.add')}
                      </Button>
                      {!actions.canAdd && (
                        <NoteLine id={addHintId}>{t('reporting.threePlUsage.extra.addDisabled')}</NoteLine>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          )}
          <tfoot>
            <tr className="bg-muted/30">
              <th scope="row" className="px-3 py-2.5 text-left font-semibold sm:px-4">
                {t('reporting.threePlUsage.bill.total')}
              </th>
              <td />
              <td />
              <MoneyCell
                value={bill.total}
                formatValue={formatValue}
                className="px-2 py-2.5 text-right font-bold sm:px-4"
              />
            </tr>
          </tfoot>
        </table>
      </div>

      {redone !== null && redone > 0 && (
        // The rule stays full width; the words keep a readable measure.
        <div className="border-t px-3 py-3 sm:px-4">
          <p className="max-w-prose text-xs text-muted-foreground">
            {t('reporting.threePlUsage.bill.footerRedone', { count: redone, owner: ownerName })}
          </p>
        </div>
      )}
    </section>
  );
}

export const ThreePlBill = React.memo(ThreePlBillImpl);
